import { useTheme } from "@mui/material";
import { getWrappingCellStyle } from "@platform/btx-grid-components";
import { HeapedCommissionEditor } from "@platform/btx-plan-designer";
import { Alert, DataGrid } from "@platform/design-system";
import { AllEnterpriseModule } from "ag-grid-enterprise";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import gridIcons from "../../gridIcons";

import { CarriersPanel } from "./CarriersPanel";
import { EditCommissionEditor } from "./formatting/EditCommissionEditor";
import { RowsPanel } from "./RowsPanel";
import {
  COVERAGE_LABEL_FIELD,
  type CarrierCommissionCell,
  type CompsGridRow,
} from "./types";

import type { MpxAgGridStateProps } from "@platform/hooks";
import type {
  CellClassParams,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  MenuItemDef,
  RowDragEndEvent,
  SideBarDef,
} from "ag-grid-community";

export interface CompsGridProps {
  rowData: CompsGridRow[];
  hiddenRowGroupIds: Set<string>;
  colDefs: ColDef<CompsGridRow>[];
  gridStateProps?: MpxAgGridStateProps;
  context?: unknown;
  onCellValueChanged?: (event: CellValueChangedEvent<CompsGridRow>) => void;
  getContextMenuItems?: (
    params: GetContextMenuItemsParams,
  ) => (DefaultMenuItem | MenuItemDef)[];
}

/**
 * Presentational AG Grid component for the comps table.
 *
 * Rows: coverages as a single combined commission row, followed by the
 * subsidy rows and the Notes row. Columns: Coverage (pinned left), Requested
 * Commission (pinned left), carriers.
 *
 * Receives pre-built `rowData` and `colDefs` from `useCompsTable` or from
 * Storybook fixture injection. Does not fetch data itself.
 */
const sideBar: SideBarDef = {
  toolPanels: [
    {
      id: "carriers",
      labelDefault: "Carriers",
      labelKey: "carriers",
      iconKey: "columns",
      toolPanel: CarriersPanel,
    },
    {
      id: "rows",
      labelDefault: "Coverages",
      labelKey: "rows",
      iconKey: "menu",
      toolPanel: RowsPanel,
    },
  ],
};

export const CompsGrid = ({
  rowData,
  hiddenRowGroupIds,
  colDefs,
  gridStateProps,
  context,
  onCellValueChanged,
  getContextMenuItems,
}: CompsGridProps): React.JSX.Element => {
  const muiTheme = useTheme();
  const [orderedRowData, setOrderedRowData] = useState(rowData);

  useEffect(() => {
    setOrderedRowData(rowData);
  }, [rowData]);

  // The external filter reads the hidden set through a ref so the AG-Grid
  // callbacks stay stable; the effect re-applies the filter when it changes.
  const gridApiRef = useRef<GridApi<CompsGridRow> | null>(null);
  const hiddenRowGroupIdsRef = useRef(hiddenRowGroupIds);

  useEffect(() => {
    hiddenRowGroupIdsRef.current = hiddenRowGroupIds;
    gridApiRef.current?.onFilterChanged();
  }, [hiddenRowGroupIds]);

  const onGridReady = useCallback((event: GridReadyEvent<CompsGridRow>) => {
    gridApiRef.current = event.api;
    event.api.onFilterChanged();
  }, []);

  const isExternalFilterPresent = useCallback(
    () => hiddenRowGroupIdsRef.current.size > 0,
    [],
  );

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<CompsGridRow>) =>
      node.data
        ? !hiddenRowGroupIdsRef.current.has(node.data.rowGroupId)
        : true,
    [],
  );

  const defaultColDef = useMemo<ColDef<CompsGridRow>>(
    () => ({
      resizable: true,
      sortable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      wrapText: true,
      wrapHeaderText: true,
      autoHeight: true,
      headerStyle: {
        textAlign: "center",
      },
      cellStyle: (params: CellClassParams<CompsGridRow>) => ({
        textAlign: "center",
        whiteSpace: "normal",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...(getWrappingCellStyle(params.data?.rowType === "notes") ?? {
          wordBreak: "break-word",
        }),
      }),
    }),
    [],
  );

  const getRowId = useCallback(
    (params: GetRowIdParams<CompsGridRow>) => params.data.rowId,
    [],
  );

  const handleRowDragEnd = useCallback(
    (event: RowDragEndEvent<CompsGridRow>) => {
      const draggedRow = event.node.data;
      if (!draggedRow || draggedRow.rowType !== "commission") {
        return;
      }

      const rowGroups = orderedRowData.reduce((groups, row) => {
        const existing = groups.get(row.rowGroupId) ?? [];
        existing.push(row);
        groups.set(row.rowGroupId, existing);
        return groups;
      }, new Map<string, CompsGridRow[]>());

      const reorderedGroupIds: string[] = [];
      event.api.forEachNode((node) => {
        const groupId = node.data?.rowGroupId;
        if (node.data?.rowType === "commission" && groupId) {
          reorderedGroupIds.push(groupId);
        }
      });

      const trailingRows = orderedRowData.filter(
        (row) => row.rowType === "subsidy" || row.rowType === "notes",
      );
      const nextRowData = [
        ...reorderedGroupIds.flatMap((groupId) => rowGroups.get(groupId) ?? []),
        ...trailingRows,
      ];

      if (
        nextRowData.length !== orderedRowData.length ||
        nextRowData.every(
          (row, index) => row.rowId === orderedRowData[index]?.rowId,
        )
      ) {
        return;
      }

      setOrderedRowData(nextRowData);
    },
    [orderedRowData],
  );

  // Delete/Backspace on a focused, editable commission cell clears it —
  // routed through the same valueSetter/onCellValueChanged save path as a
  // Save from EditCommissionEditor, so it persists identically.
  const handleCellKeyDown = useCallback(
    (event: CellKeyDownEvent<CompsGridRow>) => {
      const keyboardEvent = event.event as KeyboardEvent | undefined;
      if (keyboardEvent?.key !== "Delete" && keyboardEvent?.key !== "Backspace")
        return;
      if (event.data?.rowType !== "commission") return;
      if (!event.column.isCellEditable(event.node)) return;

      const carrierId = event.column.getColId();
      const cell = event.data[carrierId] as CarrierCommissionCell | undefined;
      if (!cell || (cell.commissionType === null && cell.amount === null)) {
        return;
      }

      event.node?.setDataValue(carrierId, null);
    },
    [],
  );

  if (orderedRowData.length === 0) {
    return (
      <Alert data-testid="comps-grid" severity="info">
        No comps data found for this RFP.
      </Alert>
    );
  }

  return (
    <DataGrid
      {...gridStateProps}
      modules={[AllEnterpriseModule]}
      containerProps={{ "data-testid": "comps-grid" }}
      context={context}
      sideBar={sideBar}
      onGridReady={onGridReady}
      isExternalFilterPresent={isExternalFilterPresent}
      doesExternalFilterPass={doesExternalFilterPass}
      onCellValueChanged={onCellValueChanged}
      onCellKeyDown={handleCellKeyDown}
      getContextMenuItems={getContextMenuItems}
      stopEditingWhenCellsLoseFocus
      enterNavigatesVerticallyAfterEdit
      sx={{
        width: "100%",
        overflow: "hidden",
        [`& .ag-header-cell:not([col-id='${COVERAGE_LABEL_FIELD}']) .ag-header-cell-label`]:
          {
            justifyContent: "center",
          },
      }}
      components={{ HeapedCommissionEditor, EditCommissionEditor }}
      rowData={orderedRowData}
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      headerHeight={60}
      rowDragManaged
      suppressMovableColumns
      suppressHorizontalScroll={false}
      icons={gridIcons}
      themeParams={{
        // `theme.vars` first so the grid text follows the colour scheme; under
        // `cssVariables`, `theme.palette.*` holds the frozen light literal.
        textColor:
          muiTheme.vars?.palette.text.primary ?? muiTheme.palette.text.primary,
        headerTextColor:
          muiTheme.vars?.palette.text.primary ?? muiTheme.palette.text.primary,
      }}
      getRowId={getRowId}
      onRowDragEnd={handleRowDragEnd}
    />
  );
};
